// Shared vocabulary for commission lines — used by the Sale detail Commissions tab
// and by the Earnings commission sheet, so a line reads the same in both places.
import { RM0 } from './format';

// Mirrors the admin portal's tag palette one-for-one (antd preset hexes), so a line
// is the same colour in both apps. Twelve types is more than the Chip tone set
// carries, hence raw hexes — the same approach STAGE_COLOR takes for the stage bar.
export const COMMISSION_TYPE_COLOR = {
  HQ_ROYALTY: '#722ed1',           // purple
  PERSONAL_COMMISSION: '#52c41a',  // green
  STANDARD_OVERRIDE: '#1890ff',    // blue
  GAP_OVERRIDE: '#13c2c2',         // cyan
  BRANCH_REMAINDER: '#eb2f96',     // magenta
  CO_AGENCY_COMMISSION: '#fa8c16', // orange
  REFERRAL_COMMISSION: '#eb2f96',  // pink (antd pink === magenta)
  PROJECT_HQ: '#fa541c',           // volcano
  PROJECT_BRANCH: '#2f54eb',       // geekblue
  PROJECT_PIC: '#faad14',          // gold
  PROJECT_LEADER: '#a0d911',       // lime
  PROJECT_AGENT: '#8c8c8c',        // default
  // Not commission — the agent's cut of the tenant's stamping fee. Coloured here
  // because it still appears as a line in the breakdown.
  TENANCY_ADMIN_FEE: '#13c2c2',        // cyan
  TENANCY_DUTY_REIMBURSEMENT: '#faad14', // gold
  // Added by HQ by hand, for money the engine cannot compute — a disbursement
  // billed with no SST that the agent keeps in full, for instance. Outside net
  // GCI, so it is not part of the split adding up.
  MANUAL_COMMISSION: '#fa541c',        // volcano
};
export const commissionColor = (type) => COMMISSION_TYPE_COLOR[type] || '#8c8c8c';

// The agent's cut of the tenant's stamping fee, not commission: the server totals
// these apart (`tenancy_amount`), outside net GCI and outside `total_amount`.
const TENANCY_TYPES = new Set(['TENANCY_ADMIN_FEE', 'TENANCY_DUTY_REIMBURSEMENT']);
export const isTenancyLine = (l) => !l.is_manual && TENANCY_TYPES.has(l.commission_type);

// Chip merges `style` over its tone, so a per-type colour needs no new tone token.
export const commissionChipStyle = (type) => {
  const c = commissionColor(type);
  return { background: `color-mix(in srgb, ${c} 14%, transparent)`, color: c };
};

// STANDARD_OVERRIDE → "Standard override", HQ_ROYALTY → "HQ royalty". The enum is
// the only label the server sends, so the humanising happens here rather than in
// three components — and the acronyms stay shouted, because "Hq royalty" is wrong.
const ACRONYMS = new Set(['HQ', 'GAP', 'PIC', 'SST']);
export const commissionTypeLabel = (type) => {
  // Named for where it came from rather than humanised ("Manual commission"):
  // the agent's question about such a line is who put it there, and the answer
  // is HQ, by hand, outside the calculated split.
  if (type === 'MANUAL_COMMISSION') return 'Added by HQ';
  const words = String(type || '').split('_').filter(Boolean);
  if (words.length === 0) return 'Commission';
  return words
    .map((w, i) => {
      if (ACRONYMS.has(w.toUpperCase())) return w.toUpperCase();
      const lower = w.toLowerCase();
      return i === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
    })
    .join(' ');
};

// 0.05 → "5%", 0.0425 → "4.25%". Null rate (fixed-amount lines) renders nothing.
export const ratePct = (rate) => {
  if (rate === null || rate === undefined || rate === '') return '';
  const p = Number(rate) * 100;
  if (!Number.isFinite(p)) return '';
  return `${Number.isInteger(p) ? p : Number(p.toFixed(2))}%`;
};

/**
 * What has actually happened to one deal's commission, in a sentence — the earnings
 * list shows this instead of a progress bar. It reads off the same three numbers the
 * old tri-colour bar was drawn from, but can't paint "nothing released" as a full bar.
 */
export const stateLine = (r) => {
  const entitled = Number(r?.entitled) || 0;
  const unlocked = Number(r?.unlocked) || 0;
  const paid = Number(r?.paid) || 0;
  if (entitled > 0 && paid >= entitled) return 'Paid in full';
  if (paid > 0) return `${RM0(paid)} of ${RM0(entitled)} paid`;
  if (entitled > 0 && unlocked >= entitled) return 'Fully released · awaiting payout';
  if (unlocked > 0) return `${RM0(unlocked)} of ${RM0(entitled)} released`;
  return 'Nothing released yet';
};
